import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import Exception from "../../../../shared/exception/Exception";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	MergeMode,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxDeleteDelete,
	ToolboxDeleteDeleteInterface,
	ToolboxMergeMerge
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import PageAddDialogFields from "../../field/page/PageAddDialogFields";
import MimeType from "../../../document/mimetype/MimeType";
import {PagesAddModeEnum, PortalPagesAdd} from "../../../../shared/models/PortalPagesAdd";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import {Base64} from "js-base64";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface PageAddDialogStatus extends PortalDialogState<PortalPagesAdd> {
	pages: number;
	documents: Array<SelectableDocument>;
}

interface SelectableDocument {
	label: string,
	value: string
}

export class PageAddDialogDefinition extends PortalDialog<PortalPagesAdd, PortalDialogProperties<PortalPagesAdd>,
	PageAddDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			documents: [],
			fieldErrors: new Map<PageAddDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.mergeDocument, Functions.getMergeDocumentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ADD_PAGE, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: PortalPagesAdd | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PageAddDialogFields.DOCUMENT)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-document">
							<Translate {...this.props} data="add_pages.documents.document" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-document" options={this.state.documents}
									  value={FunctionHelper.applyValue(parameter?.data?.value, "")}
									  onChange={(e) => this.onChange(
										  PageAddDialogFields.DOCUMENT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PageAddDialogFields.OPERATION)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-operation">
							<Translate {...this.props} data="add_pages.mode.operation.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-operation"
									  value={FunctionHelper.applyValue(
										  parameter?.addMode, PortalPagesAdd.getAddModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "add_pages.mode.operation.end", {ns: "dialog"}
											  ),
											  value: PagesAddModeEnum.AtTheEnd
										  },
										  {
											  label: Translate.value(
												  "add_pages.mode.operation.start", {ns: "dialog"}
											  ),
											  value: PagesAddModeEnum.AtTheBeginning
										  },
										  {
											  label: Translate.value(
												  "add_pages.mode.operation.before", {ns: "dialog"}
											  ),
											  value: PagesAddModeEnum.BeforePage
										  },
										  {
											  label: Translate.value(
												  "add_pages.mode.operation.after", {ns: "dialog"}
											  ),
											  value: PagesAddModeEnum.AfterPage
										  },
										  {
											  label: Translate.value(
												  "add_pages.mode.operation.replace", {ns: "dialog"}
											  ),
											  value: PagesAddModeEnum.replace
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  PageAddDialogFields.OPERATION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(PageAddDialogFields.PAGE) ||
						parameter?.addMode === PagesAddModeEnum.AtTheBeginning ||
						parameter?.addMode === PagesAddModeEnum.AtTheEnd
					}>
						<label className="p-col-12 p-sm-4" htmlFor="control-page">
							<Translate {...this.props} data="add_pages.mode.page" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8"
							 hidden={!this.state.fields?.contains(PageAddDialogFields.PAGE)}>
							<InputNumber inputId="control-page" showButtons={true} step={1} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter?.page, PortalPagesAdd.getPageDefault()
										 )}
										 min={PortalPagesAdd.getPageMin()} max={this.state.pages}
										 onChange={(e) => this.onChange(
											 PageAddDialogFields.PAGE, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PageAddDialogFields.RESET)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-reset"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-reset"
										 checked={FunctionHelper.applyValue(
											 parameter?.resetMetadata, ToolboxMergeMerge.getResetMetadataDefault()
										 )}
										 onChange={(e) => this.onChange(
											 PageAddDialogFields.RESET, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-reset">
								<Translate {...this.props} data="add_pages.mode.reset.label" namespace="dialog"/>
							</label>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["add-page-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.add_pages.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: PageAddDialogFields, value: any): void {
		let parameter: PortalPagesAdd = this.state.parameter!.data;

		switch (field) {
			case PageAddDialogFields.DOCUMENT:
				parameter.data!.value = value;
				break;
			case PageAddDialogFields.OPERATION:
				parameter.addMode = value;
				break;
			case PageAddDialogFields.PAGE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ToolboxMergeMerge.getPageDefault();
				}

				parameter.page = value;
				break;
			case PageAddDialogFields.RESET:
				parameter.resetMetadata = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-add-pages small"}/>
					<Translate {...this.props} data="add_pages.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalPagesAdd | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let pageCount: number = await portalDocument.getNumberOfPages();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		let documents: Array<SelectableDocument> = [];
		for (let document of this.props.context.documentManager.getDocuments()) {
			if (document.getMimeType() === MimeTypes.PDF) {
				documents.push({
					label: document.getFileName(),
					value: Base64.toBase64(document.getSourceDocumentId()!)
				} as SelectableDocument);
			}
		}

		parameter!.data!.value = documents[0].value;

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			documents: documents
		});
	};

	protected async submit(): Promise<void> {
		let parameter: PortalPagesAdd | undefined = this.state.parameter?.data;

		switch (parameter?.addMode) {
			case PagesAddModeEnum.AtTheBeginning:
				parameter!.mode = MergeMode.AtTheBeginning;
				break;
			case PagesAddModeEnum.AtTheEnd:
				parameter!.mode = MergeMode.AtTheEnd;
				break;
			case PagesAddModeEnum.AfterPage:
				parameter!.mode = MergeMode.AfterPage;
				break;
			case PagesAddModeEnum.BeforePage:
				parameter!.mode = MergeMode.BeforePage;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, async () => {
			await super.submit();

			let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
				merge: this.state.parameter!.data.toJson()
			});

			let operationData: Array<BaseToolbox> = [toolboxOperation];
			let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
			let documentManager: PortalDocumentManager = this.props.context.documentManager;
			let selection: Array<PortalDocument> = documentManager.getSelection();

			for (let portalDocument of selection) {
				try {
					await this.props.context.session.executeRequest({
						webServiceType: webServiceType,
						document: portalDocument,
						operation: operationData,
						settings: Settings.fromJson({
							pdfa: {
								pdfaEditProfile: this.props.pdfaEditProfile
							},
							signature: {
								signatureEditProfile: this.props.signatureEditProfile
							},
							compress: {
								compressProfile: this.props.compressProfile,
								compressObjects: {
									objectStreamSize: this.props.objectStreamSize
								}
							}
						} as SettingsInterface)
					})
				} catch (exception: any) {
					this.setState({
						exception: Exception.parseResultException(exception)
					});
				}
			}

			if (parameter?.addMode !== PagesAddModeEnum.replace) {
				this.hide();
				return;
			}

			let deletePageParameter: Parameter<ToolboxDeleteDelete> =
				new Parameter<ToolboxDeleteDelete>(
					ToolboxDeleteDelete.fromJson({
						pages: parameter.page!.toString()
					} as ToolboxDeleteDeleteInterface)
				);

			toolboxOperation = BaseToolbox.fromJson({
				delete: deletePageParameter.data
			});
			operationData = [toolboxOperation];

			for (let portalDocument of selection) {
				try {
					await this.props.context.session.executeRequest({
						webServiceType: webServiceType,
						document: portalDocument,
						operation: operationData,
						settings: Settings.fromJson({
							pdfa: {
								pdfaEditProfile: this.props.pdfaEditProfile
							},
							signature: {
								signatureEditProfile: this.props.signatureEditProfile
							},
							compress: {
								compressProfile: this.props.compressProfile,
								compressObjects: {
									objectStreamSize: this.props.objectStreamSize
								}
							}
						} as SettingsInterface)
					});
				} catch (exception: any) {
					if (await this.onError(exception)) {
						await this.submit();
					}
					return;
				}

				let message: Message = new Message("success.toolbox.page.add", MessageType.SUCCESS);
				message.context = {filename: '"' + portalDocument.getFileName() + '" '};
				this.props.context.getPage().addMessage(message);
				this.hide();
			}
		});
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ADD_PAGE;
	}

	protected getDefaultParameter(): Parameter<PortalPagesAdd> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const PageAddDialog = React.memo(PageAddDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalPagesAdd>>,
		nextProps: Readonly<PortalDialogProperties<PortalPagesAdd>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default PageAddDialog;